import { useState, useCallback, useEffect } from "react";
import { Box, InputAdornment, TableRow, IconButton } from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import TotalIcon from '../../../../../../assets/facility/total-icon.svg'
import NewRegistratonIcon from '../../../../../../assets/facility/new-registration.svg'
import SuspendedIcon from '../../../../../../assets/facility/suspended-icon.svg'

import {
  SummaryCardsContainer,
  SummaryCard,
  SummaryIconWrapper,
  SummaryContent,
  SummaryTitle,
  SummaryValue,
  SummaryDescription,
  FacilityListContainer,
  PageHeader,
  PageTitle,
  Filters,
  SearchInput,
  StatusTab,
  StatusTabsContainer,
  StyledTabs,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
  PaginationFooter,
  StatusTag
} from "./styles";

import {
  IFacility,
} from '../../../../../../modules/facility';

import CustomTableHeader from "../../../../../../components/CustomHeader";
import CustomPagination from "../../../../../../components/CustomPagination";
import Loader from '../../../../../../components/common/Loader';
import apiCall from "../../../../../../utils/api";


type Order = 'asc' | 'desc';

interface Column {
  id: keyof IFacility | string;
  label: string;
  numeric?: boolean;
  disableSorting?: boolean;
}

const COLUMN_MAPPING: { [key: string]: keyof IFacility | string } = {
  'ID No': 'facility_id',
  'Name': 'facility_name',
  'Type': 'facility_type_name',
  'Address': 'address',
  'Contact Person': 'contact_person',
  'Designation': 'contact_person_designation',
  'Status': 'status'
};

const FACILITY_COLUMNS: Column[] = Object.entries(COLUMN_MAPPING).map(([label, id]) => ({
  id: id,
  label: label,
  numeric: id === 'facility_id',
  disableSorting: true,
}));

interface FacilityListProps {
  orgId: string | number;
}

const FacilityList: React.FC<FacilityListProps> = ({ orgId }) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [status, setStatus] = useState<string>('');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof IFacility | string>('facility_id');

  const [facilityList, setFacilityList] = useState<IFacility[]>([]);
  const [isClientPaginated, setIsClientPaginated] = useState(false);
  const [facilityListLoading, setFacilityListLoading] = useState(false);
  const [facilityListCount, setFacilityListCount] = useState(0);
  const [facilityStatistics, setFacilityStatistics] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await apiCall<any>('get', `/api/organization/superadmin/detail/${orgId}/`);
      if (response.data && response.data.data) {
        setFacilityStatistics({
          total_facilities: response.data.data.facility_count || 0,
          pending_facilities: response.data.data.pending_facility_count || 0,
          suspended_facilities: response.data.data.suspended_facility_count || 0,
          new_facilities_last_30_days: response.data.data.new_facility_count_30_days || 0,
          approved_facilities: response.data.data.approved_facility_count || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [orgId]);

  const fetchList = useCallback(async () => {
    setFacilityListLoading(true);
    try {
      const trimmedSearchTerm = searchTerm.trim();
      const response = await apiCall<any>('get', `/api/facility/list/?org=${orgId}&page=${page}&search=${trimmedSearchTerm}&status=${status}&ordering=${order === 'desc' ? '-' : ''}${String(orderBy)}`);
      if (response.data && response.data.data) {
        if (Array.isArray(response.data.data)) {
          setFacilityList(response.data.data);
          setFacilityListCount(response.data.data.length);
          setIsClientPaginated(true);
        } else {
          setFacilityList(response.data.data.results || []);
          setFacilityListCount(response.data.data.count || 0);
          setIsClientPaginated(false);
        }
      }
    } catch (error) {
      console.error('Error fetching facility list:', error);
    } finally {
      setFacilityListLoading(false);
    }
  }, [orgId, page, searchTerm, status, order, orderBy]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
    setPage(1);
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const propertyKey = property as keyof IFacility | string;
    const isAsc = orderBy === propertyKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(propertyKey);
  };

  const displayedList = isClientPaginated
    ? facilityList.slice((page - 1) * rowsPerPage, page * rowsPerPage)
    : facilityList;

  return (
    <Box>
      {facilityListLoading && <Loader open={facilityListLoading} />}
      <SummaryCardsContainer>
        <SummaryCard>
          <SummaryIconWrapper colorName="primary">
            <img src={TotalIcon} alt="Total Facilities" />
          </SummaryIconWrapper>
          <SummaryContent>
            <SummaryTitle>Total No of Facilities</SummaryTitle>
            <SummaryValue>{statsLoading ? '0' : facilityStatistics?.total_facilities ?? 0}</SummaryValue>
            <SummaryDescription>{statsLoading ? '0' : `${facilityStatistics?.new_facilities_last_30_days ?? 0} New in last 30 days`}</SummaryDescription>
          </SummaryContent>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconWrapper colorName="info">
            <img src={NewRegistratonIcon} alt="New Registration Facilities" />
          </SummaryIconWrapper>
          <SummaryContent>
            <SummaryTitle>New Registration</SummaryTitle>
            <SummaryValue>{statsLoading ? '0' : facilityStatistics?.new_facilities_last_30_days ?? 0}</SummaryValue>
            <SummaryDescription>Last 30 Days</SummaryDescription>
          </SummaryContent>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconWrapper colorName="error">
            <img src={SuspendedIcon} alt="Suspended Facilities" />
          </SummaryIconWrapper>
          <SummaryContent>
            <SummaryTitle>Suspended Facilities</SummaryTitle>
            <SummaryValue>{statsLoading ? '0' : facilityStatistics?.suspended_facilities ?? 0}</SummaryValue>
            <SummaryDescription>Require Action</SummaryDescription>
          </SummaryContent>
        </SummaryCard>
      </SummaryCardsContainer>


      <FacilityListContainer>
        <PageHeader>
          <PageTitle>Facility List</PageTitle>
          <Filters>
            <SearchInput
              placeholder="Search by Name, ID..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(event) => {
                  setSearchTerm(event.target.value as string);
                  setPage(1);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm && (
                      <IconButton
                        aria-label="clear search"
                        onClick={() => setSearchTerm('')}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    )}
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Filters>
        </PageHeader>

        <StatusTabsContainer>
          <StyledTabs value={tabValue} onChange={handleTabChange} aria-label="status tabs">
            <StatusTab
              label={"All (" + (facilityStatistics?.total_facilities || 0) + ")"}
              onClick={() => handleStatusChange('')}
            />
            <StatusTab
              label={"Pending (" + (facilityStatistics?.pending_facilities || 0) + ")"}
              onClick={() => handleStatusChange('pending')}
            />
            <StatusTab
              label={"Suspended (" + (facilityStatistics?.suspended_facilities || 0) + ")"}
              onClick={() => handleStatusChange('suspended')}
            />
            <StatusTab
              label={"Approved (" + (facilityStatistics?.approved_facilities || 0) + ")"}
              onClick={() => handleStatusChange('approved')}
            />
          </StyledTabs>
        </StatusTabsContainer>

        <StyledTableContainer>
          <StyledTable stickyHeader>
            <CustomTableHeader
              columns={FACILITY_COLUMNS}
              order={order}
              orderBy={orderBy as string}
              onRequestSort={handleRequestSort}
            />

            <StyledTableBody>
              {!facilityListLoading && !displayedList.length ? (
                <TableRow>
                  <StyledTableCell colSpan={FACILITY_COLUMNS.length} align="center">
                    No data available
                  </StyledTableCell>
                </TableRow>
              ) : (
                displayedList.map((facility) => (
                  <TableRow
                    key={facility.facility_id}
                    hover
                    sx={{ cursor: "default" }}
                  >
                    <StyledTableCell>#{facility.facility_id}</StyledTableCell>
                    <StyledTableCell>{facility.facility_name}</StyledTableCell>
                    <StyledTableCell>{facility.facility_type_name || 'N/A'}</StyledTableCell>
                    <StyledTableCell>{facility.address}</StyledTableCell>
                    <StyledTableCell>{facility.contact_person}</StyledTableCell>
                    <StyledTableCell>{facility.contact_person_designation}</StyledTableCell>
                    <StyledTableCell>
                      <StatusTag status={facility.status}>
                        {facility.status}
                      </StatusTag>
                    </StyledTableCell>
                  </TableRow>
                ))
              )}
            </StyledTableBody>
          </StyledTable>
        </StyledTableContainer>
      </FacilityListContainer>
      <PaginationFooter>
        <Box>
          Showing {displayedList?.length || 0} of {facilityListCount} results
        </Box>
        <CustomPagination
          totalPages={Math.ceil(facilityListCount / rowsPerPage)}
          currentPage={page}
          onPageChange={(newPage: number) => setPage(newPage)}
        />
      </PaginationFooter>
    </Box>
  )
}

export default FacilityList;
